import { useParams, Link } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Loader2 } from "lucide-react";

import { GET_SUBJECT } from "@/graphql/queries/subjects";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { skillsLabel } from "@/lib/students";

export default function SubjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, loading } = useQuery(GET_SUBJECT, {
    variables: { id },
    skip: !id,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const subject = data?.subject;

  if (!subject) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Dalykas nerastas</p>
        <Button asChild className="mt-4">
          <Link to="/subjects">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  // Group skills by grade
  const skillsByGrade = subject.skills.reduce(
    (acc: Record<number, any[]>, skill: any) => {
      const grade = skill.grade ?? 1;
      if (!acc[grade]) {
        acc[grade] = [];
      }
      acc[grade].push(skill);
      return acc;
    },
    {} as Record<number, any[]>
  );

  const grades = Object.keys(skillsByGrade)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/subjects">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{subject.name}</h1>
          <p className="text-muted-foreground">
            {skillsLabel(subject.skills.length)}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {grades.map((grade) => (
          <Card key={grade}>
            <CardHeader>
              <CardTitle className="text-lg">{grade} klasė</CardTitle>
              <CardDescription>
                {skillsLabel(skillsByGrade[grade].length)} šioje klasėje
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {skillsByGrade[grade].map((skill: any) => {
                  const prerequisiteNames: string[] =
                    skill.prerequisiteNames ?? [];

                  return (
                    <div
                      key={skill.id}
                      className="p-4 rounded-lg border space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium">{skill.name}</h3>
                        <Badge variant="outline">ID: {skill.id}</Badge>
                      </div>

                      {skill.aiContext && (
                        <p className="text-sm text-muted-foreground">
                          {skill.aiContext}
                        </p>
                      )}

                      {prerequisiteNames.length > 0 && (
                        <div className="pt-2">
                          <p className="text-xs text-muted-foreground mb-1">
                            Reikalavimai:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {prerequisiteNames.map(
                              (name: string, idx: number) => (
                                <Badge
                                  key={idx}
                                  variant="secondary"
                                  className="text-xs"
                                >
                                  {name}
                                </Badge>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
